import type { State } from '../configureStore'
import { defaultState, FailedObject } from '../reducers/dashboard/default.state'
import { Dashboard as ActionTypes } from '../actionTypes'
import * as AccessGraphActions from './access.graph.actions'
import * as ReduxHelpers from '@src/utils/reduxHelpers'
import * as GraphHelpers from '@src/utils/graph.helpers'
import {
  GraphQueryMgr,
  DashBoardQueryResp,
  ResourceDetails,
  AutoCompleteRespTypeEnum
} from '@src/endpoints/services/graphQueryMgr.service'
import { Calendar } from '@src/endpoints/services/calendar.service'
import { IntentMgr } from '@src/endpoints/services/intentMgr.service'
import { includes, replace, isEmpty, findIndex, pickBy } from 'lodash'
import uuidv4 from 'uuid/v4'
import type { TotalsPublicObjects } from '../reducers/external.lists'
import type { Graph } from '@src/components/vis.js.network.vis/models'
import type { SearchResult } from '../reducers/autocomplete.query'
import { defaultHandleStatus, paramsWithFilter } from '@src/endpoints/helpers'
import type { FilterState } from '../reducers/filter'
import * as AlertApi from '@src/endpoints/openapis/saas-cam-alerts/api'
import { GetAlertsSummaryRequest } from '@src/endpoints/openapis/saas-cam-alerts/api'
import { SummaryTypeEnum } from '@src/endpoints/openapis/saas-cam-alerts'
import type { Summary } from '@src/endpoints/openapis/saas-cam-alerts'
import { AlertSeverityEnum } from '@src/endpoints/services/commonHdr.service'

// Action Creators
export type ActionTypes =
  | getStatSuccessAction
  | cleanerStatAction
  | searchResultsSuccessAction
  | searchPrivilegeResultsSuccessAction
  | searchCrossAccountResultsSuccessAction
  | searchUsageResultsSuccessAction
  | refreshCalendarSuccessAction
  | refreshCalendarFailAction
  | serverErrorsAction
  | requestPendingAction
  | requestPendingGraphAction
  | getSevCountsAction
  | getStatsDataAction

const getStatSuccess = ReduxHelpers.Common.Actions.setToState(
  ActionTypes.GET_STAT_SUCCESS,
  defaultState
)
type getStatSuccessAction = ReturnType<typeof getStatSuccess>

export const cleanerStat = ReduxHelpers.Common.Actions.cleanerState(
  ActionTypes.CLEANER_STAT,
  defaultState
)

type cleanerStatAction = ReturnType<typeof cleanerStat>

export const searchResultsSuccess = (
  graph: Graph,
  table: SearchResult[],
  objects?: ResourceDetails[],
  identities?: ResourceDetails[],
  policies?: ResourceDetails[],
  autoCompleteRespType?: AutoCompleteRespTypeEnum,
  sqlQueryUserUsage?: string,
  isPending = false
) =>
  ReduxHelpers.Actions.createPayloadAction(ActionTypes.SEARCH_RESULTS_SUCCESS, {
    graph,
    table,
    objects,
    identities,
    policies,
    autoCompleteRespType,
    sqlQueryUserUsage,
    isPending
  })
type searchResultsSuccessAction = ReturnType<typeof searchResultsSuccess>

export const searchPrivilegeResultsSuccess = (
  graph?: Graph,
  table?: SearchResult[]
) =>
  ReduxHelpers.Actions.createPayloadAction(
    ActionTypes.SEARCH_PRIVILEGE_RESULTS_SUCCESS,
    { graph, table, isPending: false }
  )

type searchPrivilegeResultsSuccessAction = ReturnType<
  typeof searchPrivilegeResultsSuccess
>

export const searchCrossAccountResultsSuccess = (
  graph?: Graph,
  table?: SearchResult[]
) =>
  ReduxHelpers.Actions.createPayloadAction(
    ActionTypes.SEARCH_CROSS_ACCOUNT_RESULTS_SUCCESS,
    { graph, table, isPending: false }
  )
type searchCrossAccountResultsSuccessAction = ReturnType<
  typeof searchCrossAccountResultsSuccess
>

export const searchUsageResultsSuccess = (
  graph?: Graph,
  table?: SearchResult[]
) =>
  ReduxHelpers.Actions.createPayloadAction(
    ActionTypes.SEARCH_USAGE_RESULTS_SUCCESS,
    { graph, table, isPending: false }
  )

type searchUsageResultsSuccessAction = ReturnType<
  typeof searchUsageResultsSuccess
>

export const refreshCalendarSuccess = (alertsList?: any[], isPending = false) =>
  ReduxHelpers.Actions.createPayloadAction(
    ActionTypes.REFRESH_CALENDAR_SUCCESS,
    { alertsList, isPending }
  )
type refreshCalendarSuccessAction = ReturnType<typeof refreshCalendarSuccess>

export const refreshCalendarFail = (error: any) =>
  ReduxHelpers.Actions.createPayloadAction(ActionTypes.REFRESH_CALENDAR_FAIL, {
    error,
    isPending: false
  })
type refreshCalendarFailAction = ReturnType<typeof refreshCalendarFail>

export const serverErrors = (errors: any) =>
  ReduxHelpers.Actions.createPayloadAction(ActionTypes.SERVER_ERRORS, {
    errors
  })

type serverErrorsAction = ReturnType<typeof serverErrors>

export const requestPending = (isPending: boolean) =>
  ReduxHelpers.Actions.createPayloadAction(ActionTypes.REQUEST_PENDING, {
    isPending
  })
type requestPendingAction = ReturnType<typeof requestPending>

export const requestPendingGraph = (isPending: boolean) =>
  ReduxHelpers.Actions.createPayloadAction(ActionTypes.REQUEST_PENDING_GRAPH, {
    isPending
  })

type requestPendingGraphAction = ReturnType<typeof requestPendingGraph>

export const getSevCounts = (sevCounts: any) =>
  ReduxHelpers.Actions.createPayloadAction(ActionTypes.GET_SEV_COUNTS, {
    sevCounts
  })
type getSevCountsAction = ReturnType<typeof getSevCounts>

export const getStatsData = (
  data: DashBoardQueryResp,
  publicObjectDict: TotalsPublicObjects
) =>
  ReduxHelpers.Actions.createPayloadAction(ActionTypes.GET_STATS_DATA, {
    data,
    publicObjectDict
  })
type getStatsDataAction = ReturnType<typeof getStatsData>

export function startGetGraph() {
  return (dispatch: any, getStore: () => State) => {
    dispatch(requestPendingGraph(true))
  }
}

interface RefreshCalendarParams {
  tenant_id?: string
  req_hdr?: {
    tid: string
  }
}

export function refreshCalendar(callback?: () => void) {
  const params: RefreshCalendarParams = {}
  params.req_hdr = {
    tid: uuidv4()
  }
  return (dispatch: any, getStore: () => State) => {
    dispatch(requestPending(true))
    Calendar.OnDemandRunFSM({ params })
      .then(res =>
        defaultHandleStatus(res, refreshCalendarFail, true, dispatch)
      )
      .then(res => {
        dispatch(refreshCalendarSuccess())
        if (callback) callback()
        dispatch(requestPending(false))
      })
      .catch((res: Response) => dispatch(refreshCalendarFail(res?.statusText)))
  }
}

export function getAllStats(hideAlerts: boolean, callback: () => void) {
  return (dispatch: any, getState: () => State) => {
    dispatch(requestPending(true))
    getTotalsAsync(hideAlerts, dispatch, getState, callback)
  }
}

export function getTotalsAsync(
  hideAlerts: boolean,
  dispatch: any,
  getState: () => State,
  callback: () => void
) {
  dispatch(getData(callback))
  if (!hideAlerts) {
    dispatch(getFailedCounts())
  }
}

export function getData(callback: () => void) {
  return (dispatch: any, getState: () => State) => {
    const { filter } = getState()
    dispatch(requestPending(true))
    GraphQueryMgr.DashBoardQuery({ params: paramsWithFilter({}, filter) })
      .then(res =>
        defaultHandleStatus(
          res,
          () => {
            dispatch(requestPending(false))
          },
          true
        )
      )
      .then(res => {
        const { result_graph } = res
        dispatch(
          AccessGraphActions.changeGraphView({
            selectedGraphView: GraphHelpers.View.access,
            callbackFn: callback,
            graphData: result_graph
          })
        )
        const publicObjectsCounts = {} as TotalsPublicObjects
        if (
          !isEmpty(res.public_objects_info) &&
          Array.isArray(res.public_objects_info)
        ) {
          res.public_objects_info.forEach(item => {
            publicObjectsCounts[item.source_type] = item.public_count
          })
        }

        dispatch(getStatsData(res, publicObjectsCounts))
      })
      .catch((res: Response) => dispatch(requestPending(false)))
  }
}

export const getQueryWithPrivilegeBySearchQuery = (searchQuery: string) => {
  if (
    includes(searchQuery, 'with-access') &&
    !includes(searchQuery, 'with-privilege')
  ) {
    return replace(
      searchQuery,
      'with-access',
      'with-privilege admin with-access'
    )
  }
  return `${searchQuery} with-privilege admin`
}

export const getQueryByCrossAccount = (searchQuery: string) => {
  if (
    includes(searchQuery, 'with-access') &&
    !includes(searchQuery, 'x-account')
  ) {
    const searchQueryArr = searchQuery.split(' ')
    const foundWithAccessIndex = findIndex(
      searchQueryArr,
      str => str === 'with-access'
    )
    searchQueryArr.splice(foundWithAccessIndex + 2, 0, 'x-account')
    return searchQueryArr.join(' ')
  }
  return `${searchQuery} x-account`
}

export function getFailedCounts(params = {} as any) {
  return (dispatch, getState: () => State) => {
    IntentMgr.GetFailedCountsByRegion(params)
      .then(res =>
        defaultHandleStatus(
          res,
          () => {
            dispatch(requestPending(false))
          },
          true
        )
      )
      .then(res => {
        const regions: FailedObject[] = []
        const counts = res.counts || []
        counts.forEach(item => {
          if (isEmpty(item.region_info)) return
          if (item.region_info.name === 'Global') {
            const summary = `Global failed objects: ${item.count}`
            dispatch(getStatSuccess('mapSummary', summary))
            return
          }
          const { region_info = {} } = item
          const { coordinates = {} } = region_info
          regions.push({
            name: `${region_info.code},
              ${region_info.name},
              ${item.count} failed object${item.count > 1 ? 's' : ''}`,
            coordinates: [coordinates.longitude, coordinates.latitude],
            radius: item.count,
            id: region_info.code
          })
        })
        dispatch(getStatSuccess('failedObjects', regions))
        dispatch(requestPending(false))
      })
      .catch((res: Response) => dispatch(requestPending(false)))
  }
}

export function getAlertsCountLegacy(callback?: (data?: any) => void) {
  return (dispatch: any, getStore: () => State) => {
    const { filter } = getStore()
    dispatch(requestPending(true))
    IntentMgr.GetAlertCount({
      params: paramsWithFilter({}, filter),
      options: { signal: null }
    })
      .then(res =>
        defaultHandleStatus(
          res,
          () => {
            dispatch(requestPending(false))
          },
          true
        )
      )
      .then(res => {
        dispatch(
          getSevCounts({
            high_count: res.high_count || 0,
            medium_count: res.medium_count || 0,
            low_count: res.low_count || 0
          })
        )
        if (callback) callback(res)
        dispatch(requestPending(false))
      })
      .catch((res: Response) => dispatch(requestPending(false)))
  }
}

export function getAlertsCountOpenApi(
  params?: GetAlertsSummaryRequest,
  callback?: (data?: any) => void
) {
  return (dispatch: any, getStore: () => State) => {
    dispatch(requestPending(true))
    AlertApi.getAlertsSummary({
      ...params
    })
      .then(res =>
        defaultHandleStatus(
          res,
          () => {
            dispatch(requestPending(false))
          },
          true
        )
      )
      .then(res => {
        dispatch(
          getSevCounts({
            high_count:
              ((res as Summary[]) || []).find(
                a => a.type === AlertSeverityEnum.ALERT_SEVERITY_HIGH
              )?.count || 0,
            medium_count:
              ((res as Summary[]) || []).find(
                a => a.type === AlertSeverityEnum.ALERT_SEVERITY_MEDIUM
              )?.count || 0,
            low_count:
              ((res as Summary[]) || []).find(
                a => a.type === AlertSeverityEnum.ALERT_SEVERITY_LOW
              )?.count || 0
          })
        )
        if (callback) callback(res)
        dispatch(requestPending(false))
      })
      .catch((res: Response) => dispatch(requestPending(false)))
  }
}

export interface GetAlertsCountOptions {
  objectURI?: string
  callback?: (data?: any) => void
  useV2: boolean
}

export const getAlertsCount =
  (options: GetAlertsCountOptions) =>
  (dispatch: any, getStore: () => State) => {
    const { filter } = getStore()
    const { objectURI = [], useV2 } = options || {}

    const accountIds = handleFilterData(filter)
    const params: GetAlertsSummaryRequest = {
      ...pickBy(
        {
          accountIds,
          objectURI
        },
        value => !isEmpty(value)
      ),
      type: SummaryTypeEnum.Severity
    }

    if (useV2) {
      dispatch(getAlertsCountOpenApi(params, options?.callback))
    } else {
      dispatch(getAlertsCountLegacy(options?.callback))
    }
  }

export function handleFilterData(filter: FilterState) {
  const { currentAccount } = filter
  const { id } = currentAccount || {}

  if (id) {
    return [id]
  }

  return null
}
